/**
 * Wire shape of one conversation trace — an append-only record of an engine moment that decorates
 * the investor transcript, either as a chip on a Tallo message (messageId set) or as a standalone
 * event row between messages (messageId null). Mirrors the BE ConversationTraceDto.
 *
 * `kind` is a plain string on the wire (new kinds ship without a migration), so every consumer must
 * fall back to a humanized kind string for values it does not know yet — never crash.
 */
export interface ConversationTraceDto {
  id: string;
  kind: string;
  actor: string;
  payload: Record<string, unknown>;
  messageId: number | null;
  createdAt: string;
}

export enum ConversationTraceKind {
  NEW_LEAD = 'new-lead',
  STRATEGY = 'strategy',
  QUALIFICATION = 'qualification',
  FOLLOW_UP = 'follow-up',
  SCHEDULING = 'scheduling',
  OWNER_ACTION = 'owner-action',
  ESCALATION_ANSWER = 'escalation-answer',
  IDENTITY_VERIFICATION = 'identity-verification',
  APPLICATION_INVITE_SENT = 'application-invite-sent',
  APPLICATION_UPDATE_REQUESTED = 'application-update-requested',
  APPLICATION_SUBMITTED = 'application-submitted',
  APPLICATION_REQUESTED = 'application-requested',
}

export type StrategyTraceOutcome = 'answered' | 'no_answer' | 'escalated';

export interface StrategyTracePayload {
  /** BE NextStep enum key (e.g. "DISCUSS_CREDIT_SCORE") — the stable id the copy map keys on. */
  step: string;
  /** Verbatim slice of the renter's message this strategy was answering (≤500 chars). */
  relatedText: string;
  outcome: StrategyTraceOutcome;
  /**
   * Owner-facing questions the strategy escalated mid-execution while still replying. With
   * outcome 'escalated' the reply was pure holding text; with 'answered' the turn was partial —
   * some questions answered, these ones sent to the owner.
   */
  escalatedQuestions?: string[];
  /**
   * True when a screening strategy asked the renter for the fact instead of reading it from the
   * message — renders as "asking X", not "X ✓". Absent on old rows (they keep the collected look).
   */
  asked?: boolean;
  /** Set when the strategy ran in the objective-opportunity pass — Tallo raised the topic itself. */
  pass?: 'objective';
  /**
   * Which branch of a multi-leaf step ran — HANDLE_SHOWING_LOGISTICS: share_access, let_in_now,
   * replace_code, confirm_attendance, access_pending, access_failure_escalated, verification_required,
   * verification_failed, logistics_reply. Absent on other steps and on older rows.
   */
  leaf?: string;
  /** True when the renter confirmed they are coming in this turn. */
  renterConfirmed?: boolean;
  /** The showing the step acted on. */
  showingId?: string;
}

export interface QualificationTracePayload {
  qualified: boolean;
  reason?: string;
  /** Renter-side values are free-text screening answers ("around 670"), not parsed numbers. */
  creditScore?: string;
  minCreditScore?: number;
  monthlyIncome?: string;
  minimumIncome?: string;
  moveInDate?: string;
  desiredLeasingDate?: string;
}

export interface SchedulingTracePayload {
  phase: 'slot_proposed' | 'request_sent';
  /** Proposed showing start time, ISO UTC. */
  time: string;
  tourType?: string;
}

export interface FollowUpTracePayload {
  /** BE FollowUpTypeEnum value, e.g. "Renter Stopped Responding". */
  followUpType: string;
  channelSwitched?: boolean;
  fromChannel?: string;
  toChannel?: string;
  /** Post-tour check-in only: the self-guided renter never got the door access, so Tallo offered another time. */
  noAccess?: boolean;
}

export interface NewLeadTracePayload {
  /** BE PropertyInquirySource value, e.g. "Zillow" or "Tallo". */
  source: string;
}

/** Owner answered an escalated question; the trace rides Tallo's relayed message. */
export interface EscalationAnswerTracePayload {
  /** The owner-facing question this answer closes out. */
  question: string;
  /** The owner's raw answer, before Tallo rephrased it for the renter. */
  answer: string;
}

/**
 * `via: 'apply_link'`: the renter applied through a seat's apply link and the application went out on its own, so
 * the rail says the renter applied rather than that the owner sent it. Absent on every send an owner made.
 */
export interface ApplicationInviteSentTracePayload {
  via?: 'apply_link';
}

/**
 * The renter asked for the application through an apply link and it did not go out on its own (the link was
 * paused, or the screening step failed): it waits for the owner to send it. Always a standalone row.
 */
export interface ApplicationRequestedTracePayload {
  via: 'apply_link';
}

/**
 * Owner asked the applicant for more information on a submitted application; the trace rides the
 * Tallo message that relayed the request, and the bundle re-opens until the renter resubmits.
 */
export interface ApplicationUpdateRequestTracePayload {
  /** The owner's request text — already past the BE Fair-Housing screen. */
  message: string;
}

/**
 * The primary applicant submitted the application; always a standalone row (nothing is said to the
 * renter at that moment). `resubmitted` — it answers an earlier "request more info".
 */
export interface ApplicationSubmittedTracePayload {
  resubmitted: boolean;
}

export type IdentityVerificationTracePhase = 'link_sent' | 'verified' | 'declined';

/**
 * link_sent rides the Tallo message that carried the verification link; verified/declined arrive
 * standalone when the ID-verification vendor reports the outcome. Verification is renter-scoped:
 * one pass covers every property, so a verified row can land without a link_sent in this
 * conversation when the renter verified elsewhere.
 */
export interface IdentityVerificationTracePayload {
  phase: IdentityVerificationTracePhase | string;
}

export type OwnerActionTraceAction =
  | 'approved'
  | 'declined'
  | 'rescheduled'
  | 'canceled'
  | 'expired'
  | 'auto_canceled'
  /** The owner asked a renter who wanted to apply without touring to tour first. */
  | 'application_request_tour_first';

export interface OwnerActionTracePayload {
  action: OwnerActionTraceAction | string;
  showingTime?: string;
  proposedTime?: string;
  /** application_request_tour_first: the note exactly as the renter received it. */
  message?: string;
  /** Only ever a reason the BE already passed through its FHA safe-to-share check. */
  reason?: string;
  canceledBy?: 'owner' | 'renter' | 'system';
  tourType?: string;
}
